import "./Portfolio.css";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import PortfolioNavbar from "../portfolio-components/PortfolioNavbar";
import { Helmet } from "react-helmet";
import { computeHoldingMetrics, normalizeQuote, unrealizedGain, totalReturnPct, isIncomeType, incomeAmounts } from "../../lib/portfolioMetrics";
import { useTransactions } from "../../lib/useTransactions";
import { cachedFetch, TTL } from "../../lib/clientCache";

function fmtDate(tsMs) {
  if (!tsMs) return "-";
  return new Date(tsMs).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtNum(val, decimals = 2) {
  if (val === null || val === undefined) return "-";
  return Number(val).toFixed(decimals);
}

function gainClass(val) {
  if (val === null || val === undefined || val === 0) return undefined;
  return val > 0 ? "text-success" : "text-danger";
}

function SummaryTab({ ticker, exchange, priceData, dividendData, transactions }) {
  const divEntries = dividendData?.exDates ?? [];
  const upcoming   = divEntries.filter((e) => e.isFuture);

  // Received dividends come from the imported broker transactions — the
  // authoritative record of what was actually paid (account currency).
  const received = (transactions || [])
    .filter((tx) => isIncomeType(tx.type))
    .slice()
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  const totalIncome = received.reduce(
    (sum, tx) => sum + (incomeAmounts(tx).net ?? 0), 0
  );

  const { price, currency } = priceData
    ? normalizeQuote(priceData.price ?? null, priceData.currency ?? "-")
    : { price: null, currency: "-" };

  const metrics = transactions ? computeHoldingMetrics(transactions) : null;
  const marketValue = metrics && price !== null ? price * metrics.quantity : null;
  const capitalGain = metrics ? unrealizedGain(marketValue, metrics.costBasis) : null;
  const returnPct   = metrics ? totalReturnPct(capitalGain, totalIncome, metrics.costBasis) : null;

  return (
    <div>
      <div className="mb-3">
        <h5>{ticker} — {exchange}</h5>
        <p>
          Current price:{" "}
          <strong>{price !== null ? `${fmtNum(price)} ${currency}` : "Fetching..."}</strong>
        </p>
      </div>

      {metrics && (metrics.quantity > 0 || metrics.disposals.length > 0) && (
        <div className="card mb-3" style={{ maxWidth: 420 }}>
          <div className="card-header fw-bold">Holding Performance</div>
          <div className="card-body py-2">
            <div className="d-flex justify-content-between">
              <span className="text-muted">Quantity held</span>
              <span>{fmtNum(metrics.quantity, 4)}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-muted">Average cost</span>
              <span>{fmtNum(metrics.avgCost, 4)}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-muted">Cost base</span>
              <span>{fmtNum(metrics.costBasis)}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-muted">Market value</span>
              <span>{fmtNum(marketValue)}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-muted">Capital gain</span>
              <span className={gainClass(capitalGain)}>{fmtNum(capitalGain)}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-muted">Realized gain</span>
              <span className={gainClass(metrics.realizedGain)}>{fmtNum(metrics.realizedGain)}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-muted">Dividend income</span>
              <span className={gainClass(totalIncome)}>{fmtNum(totalIncome)}</span>
            </div>
            <hr className="my-1" />
            <div className="d-flex justify-content-between">
              <span className="text-muted">Total return</span>
              <strong className={gainClass(returnPct)}>
                {returnPct !== null ? `${fmtNum(returnPct)}%` : "-"}
              </strong>
            </div>
          </div>
        </div>
      )}

      <h6>Dividend Log — Received</h6>
      {received.length === 0 ? (
        <p className="text-muted">No dividend history. Import a Freetrade CSV to populate this.</p>
      ) : (
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Pay Date</th>
              <th>Ex-Date</th>
              <th>Eligible Shares</th>
              <th>Per Share</th>
              <th>Gross</th>
              <th>Net</th>
            </tr>
          </thead>
          <tbody>
            {received.map((tx, i) => {
              const { gross, net } = incomeAmounts(tx);
              return (
                <tr key={i}>
                  <td>{fmtDate(tx.dividendPayDate || tx.timestamp)}</td>
                  <td>{fmtDate(tx.dividendExDate)}</td>
                  <td>{fmtNum(tx.dividendEligibleQuantity, 4)}</td>
                  <td>{fmtNum(tx.dividendAmountPerShare, 4)}</td>
                  <td>{fmtNum(gross)}</td>
                  <td>{fmtNum(net)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5}><strong>Total net income</strong></td>
              <td><strong>{fmtNum(totalIncome)}</strong></td>
            </tr>
          </tfoot>
        </table>
      )}

      {upcoming.length > 0 && (
        <>
          <h6 className="mt-3">Upcoming (projected)</h6>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Ex-Date</th>
                <th>Shares Held</th>
                <th>Per Share</th>
                <th>Est. Total</th>
              </tr>
            </thead>
            <tbody>
              {upcoming.map((e) => (
                <tr key={e.exDate} className="fst-italic text-muted">
                  <td>{fmtDate(e.exDate)}</td>
                  <td>{fmtNum(e.sharesHeld, 4)}</td>
                  <td>{fmtNum(e.perShare, 4)}</td>
                  <td>{fmtNum(e.totalGross, 4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function TransactionLogTab({ transactions, loading, error }) {
  if (loading) return <p>Loading transactions...</p>;
  if (error)   return <p className="text-danger">Error: {error}</p>;
  if (!transactions || transactions.length === 0) {
    return <p className="text-muted">No transactions found. Import a Freetrade CSV to see history.</p>;
  }

  return (
    <table className="table table-sm">
      <thead>
        <tr>
          <th>Date</th>
          <th>Type</th>
          <th>Direction</th>
          <th>Qty</th>
          <th>Price/Share</th>
          <th>Total</th>
          <th>Currency</th>
        </tr>
      </thead>
      <tbody>
        {transactions.map((tx, i) => {
          const isDividend = isIncomeType(tx.type) || tx.type === "INTEREST";
          return (
            <tr key={i}>
              <td>{fmtDate(tx.timestamp)}</td>
              <td>{tx.type}</td>
              <td>{isDividend ? "—" : (tx.buySell || "-")}</td>
              <td>
                {isDividend
                  ? fmtNum(tx.dividendEligibleQuantity, 4)
                  : fmtNum(tx.quantity, 4)}
              </td>
              <td>
                {isDividend
                  ? fmtNum(tx.dividendAmountPerShare, 4)
                  : fmtNum(tx.pricePerShare, 4)}
              </td>
              <td>{fmtNum(tx.totalAmount, 4)}</td>
              <td>{tx.accountCurrency || tx.instrumentCurrency || "-"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function StockDetailView() {
  const { ticker } = useParams();

  const [tab,          setTab]          = useState("summary");
  const [priceData,    setPriceData]    = useState(null);
  const [dividendData, setDividendData] = useState(null);
  const [exchange,     setExchange]     = useState("");

  // Full history comes from the client-cached Firestore read; the per-ticker
  // log and performance card just filter it (no extra backend call).
  const { transactions: allTransactions, error: txErrorObj, loading: txLoading } = useTransactions();
  const transactions = useMemo(
    () => (allTransactions ? allTransactions
      .filter((tx) => tx.ticker === ticker)
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)) : null),
    [allTransactions, ticker]
  );
  const txError = txErrorObj ? txErrorObj.message : null;

  const fetchSummaryData = useCallback(async () => {
    if (!ticker) return;
    const fns = getFunctions();

    // Resolve exchange from the (client-cached) portfolio
    try {
      const portfolio = await cachedFetch("portfolio", TTL.PORTFOLIO, async () => {
        const res = await httpsCallable(fns, "getPortfolio")();
        return JSON.parse(res.data);
      });
      const stockEntry = (Object.entries(portfolio) || []).find(([, v]) => Object.keys(v)[0] === ticker);
      const exch = stockEntry ? stockEntry[1][ticker]?.exchange || "" : "";
      setExchange(exch);

      // Fetch price (client-cached per ticker)
      cachedFetch(
        `prices:${ticker}`,
        TTL.PRICES,
        async () => {
          const r = await httpsCallable(fns, "getPrices")({ query: ticker });
          return JSON.parse(r.data);
        },
        (parsed) => parsed?.[ticker]?.price !== null && parsed?.[ticker]?.price !== undefined
      )
        .then((parsed) => { if (parsed[ticker]) setPriceData(parsed[ticker]); })
        .catch(console.error);

      // Fetch dividend projections (client-cached, shared with the grid)
      if (exch) {
        cachedFetch(`dividends:${ticker}`, TTL.DIVIDENDS, async () => {
          const r = await httpsCallable(fns, "getDividendHistory")({ tickers: [{ ticker, exchange: exch }] });
          return JSON.parse(r.data);
        })
          .then((parsed) => { if (parsed[ticker]) setDividendData(parsed[ticker]); })
          .catch(console.error);
      }
    } catch (err) {
      console.error("StockDetailView summary fetch error:", err);
    }
  }, [ticker]);

  useEffect(() => { fetchSummaryData(); }, [fetchSummaryData]);

  // Live price updates: prices may be filled in asynchronously by the
  // backend ingestion queue, so listen to the cache entry directly.
  useEffect(() => {
    if (!ticker) return undefined;
    const unsub = onSnapshot(
      doc(getFirestore(), "priceCache", ticker),
      (snap) => { if (snap.exists()) setPriceData(snap.data()); },
      (err) => console.error("Price listener error:", err)
    );
    return unsub;
  }, [ticker]);

  return (
    <div className="StockDetailView">
      <Helmet>
        <title>{ticker} — Stock Detail</title>
      </Helmet>
      <div className="row">
        <div className="sidebar col-md-auto">
          <PortfolioNavbar />
        </div>

        <div className="main-content col">
          <h2>{ticker}</h2>

          {/* Tab navbar */}
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                className={`nav-link${tab === "summary" ? " active" : ""}`}
                onClick={() => setTab("summary")}
              >
                Summary
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link${tab === "transactions" ? " active" : ""}`}
                onClick={() => setTab("transactions")}
              >
                Transaction Log
              </button>
            </li>
          </ul>

          {tab === "summary" && (
            <SummaryTab
              ticker={ticker}
              exchange={exchange}
              priceData={priceData}
              dividendData={dividendData}
              transactions={transactions}
            />
          )}

          {tab === "transactions" && (
            <TransactionLogTab
              transactions={transactions}
              loading={txLoading}
              error={txError}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default StockDetailView;
